<template>
  <div ref="contentWrapper" :class="[{'absolute w-full pr-5': isClass }]">
    <div v-if="status === 'loading'" class="flex items-center justify-center py-8 text-center">
      <UiLoader size="40px" class="mr-3" />
      {{ $t('feedback.loading') }}
    </div>
    <div v-else-if="status === 'error'" class="w-full">
      <UiAlert
        type="danger"
        class="mb-6 max-w-2xl mx-auto"
      >
        <template #icon>
          <UiIcon icon="file-alert" size="42" />
        </template>
        <div class="font-semibold text-lg">{{ $t('dialogs.ops') }}</div>
        <div class="text-base mb-4">{{ $t('feedback.errorLoadObject', {object: $t('labels.pdf')}) }}</div>
      </UiAlert>
    </div>

    <div class="relative">
      <div v-if="downloadInProgress" class="absolute inset-0 z-50 bg-black/80 flex justify-center">
        <div class="text-center p-4">
          <div class="text-lg font-semibold">{{ $t('feedback.preparingDownload') }}</div>
          <div class="h-20">
            <UiLoader size="80px" />
          </div>
        </div>
      </div>
      <div
        v-if="render" ref="pdfWrapper" :key="timeFix"
        :class="[
          'relative flex flex-col items-center',
          { 'fixed inset-0 z-50 bg-[#F5F5F5] overflow-auto': isFullscreen }
        ]"
        :style="wrapperStyle"
      >
        <div
          v-if="isLoading && status !== 'error' && status !== 'awaiting-password'"
          class="absolute inset-0 z-50 bg-white/70 flex items-center justify-center"
        >
          <UiIcon icon="loader" class="animate-spin text-4xl text-primary-500" />
        </div>

        <!-- Toolbar fixa no topo -->
        <div class="w-full flex-shrink-0">
          <PdfToolbar ref="toolbarRef" v-model="page" :pageCount="pages" :max-page="maxPageReached" :all-pages-view="showAllPages">
            <template #actions>
              <div class="text-right flex gap-2 justify-end">
                <!--  -->
                <UiDropdown>
                  <UiButton type="ghost" icon="zoom-scan" class="p-2">
                    {{ zoom === -1 ? 'Auto' : `${Math.round(zoom * 100)}%` }}
                  </UiButton>
                  <template #content>
                    <div class="p-1 text-neutral-200 text-center">
                      <div class="flex items-center gap-2" @click.stop="() => {}">
                        <UiButton type="ghost" class="p-1" v-tooltip="{ text: $t('button.zoomOut') }" @click="setZoom(zoom - 0.1)">
                          <UiIcon icon="zoom-out" />
                        </UiButton>
                        <div class="text-sm w-[60px]">{{ zoom === -1 ? '100%' : Math.round(zoom * 100) }}%</div>
                        <UiButton type="ghost" class="p-1" v-tooltip="{ text: $t('button.zoomIn') }" @click="setZoom(zoom + 0.1)">
                          <UiIcon icon="zoom-in" />
                        </UiButton>
                      </div>
                      <hr class="my-1" />
                      <div
                        class="px-2 py-1.5 hover:bg-gray-100 hover:text-[--c-primary-700] cursor-pointer rounded"
                        :class="{ 'text-[--c-primary-300]': -1 === zoom }"
                        @click="setZoom(-1)"
                      >
                        Automático
                      </div>
                      <hr class="my-1" />
                      <div
                        v-for="z in [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 4]"
                        :key="z"
                        class="px-2 py-1.5 hover:bg-gray-100 hover:text-[--c-primary-700] cursor-pointer rounded"
                        :class="{ 'text-[--c-primary-300]': z === zoom }"
                        @click.stop="setZoom(z)"
                      >
                        {{ Math.round(z * 100) }}%
                      </div>
                    </div>
                  </template>
                </UiDropdown>

                <!-- Rotação -->
                <UiDropdown>
                  <UiButton type="ghost" icon="rotate-rectangle" class="p-2">
                    {{ rotation }}°
                  </UiButton>
                  <template #content>
                    <div class="p-1 text-neutral-200 text-center">
                      <div
                        v-for="r in [0, 90, 180, 270]"
                        :key="r"
                        class="px-2 py-1.5 hover:bg-gray-100 hover:text-[--c-primary-700] cursor-pointer rounded"
                        :class="{ 'text-[--c-primary-300]': r === rotation }"
                        @click.stop="rotation = r"
                      >
                        {{ r }}°
                      </div>
                    </div>
                  </template>
                </UiDropdown>

                <div v-if="allowDownload" class="tooltip tooltip-bottom" :data-tip="$t('button.download', { text: '(Ctrl + S)' })">
                  <UiButton type="ghost" class="p-2" @click="savePdf">
                    <UiIcon icon="download" />
                  </UiButton>
                </div>
                <div v-if="false && (allowPrinting || allowDownload)" class="tooltip tooltip-bottom" :data-tip="$t('button.print', { text: '(Ctrl + P)' })">
                  <UiButton type="ghost" class="p-2" @click="print">
                    <UiIcon icon="printer" />
                  </UiButton>
                </div>
                <div class="tooltip tooltip-bottom" :data-tip="!isFullscreen ? $t('button.fullscreen', ['', '(Alt + Enter)']) : $t('button.fullscreen', [$t('button.exit'), '(Alt + Enter)'])">
                  <UiButton type="ghost" class="p-2" @click="toggle()">
                    <UiIcon :icon="!isFullscreen ? 'maximize' : 'minimize'" />
                  </UiButton>
                </div>
                <div class="tooltip tooltip-bottom" :data-tip="showAllPages ? $t('button.singlePageView') : $t('button.allPagesView')">
                  <UiButton type="ghost" class="p-2" @click="toggleViewMode">
                    <UiIcon :icon="showAllPages ? 'article' : 'layout-list'" />
                  </UiButton>
                </div>
              </div>
            </template>
          </PdfToolbar>
        </div>

        <div v-if="passwordManager.requiresPassword" class="text-left bg-neutral-300 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-800">
          <div v-if="passwordManager.requiresPassword" class="flex items-center gap-2 bg-red-300 dark:bg-red-800 p-2 rounded-md mb-4">
            <div class="shrink-0">
              <UiIcon icon="lock" size="40" />
            </div>
            <div>
              <div class="font-semibold text-base">{{ $t('pdf.protectedDocument') }}</div>
              <div class="text-sm">{{ $t('pdf.fileProtected') }}</div>
            </div>
          </div>

          <div class="text-sm mb-2 font-semibold">{{ $t('pdf.enterPassword') }}</div>

          <div v-if="passwordManager.wrong" class="text-red-500 mb-4">
            {{ $t('pdf.checkPassword') }}
          </div>

          <UiInput
            v-model="passwordManager.password"
            class="w-full"
            @keyup.enter="passwordManager.submit"
          />

          <div class="text-right">
            <UiButton
              type="primary"
              class="mt-4 px-4 py-2 uppercase" 
              @click="passwordManager.submit">
              {{ $t('button.confirm') }}
            </UiButton>
          </div>
        </div>

        <div v-if="showPdfContainer" ref="pdfContainer" class="space-y-4 p-4 w-full h-full overflow-auto">
          <div
            v-for="p in (showAllPages ? pages : 1)"
            :key="p"
            :id="`pdf-page-${p}`"
            class="relative p-wrapper"
            style="--scale-round-x: 5px; --scale-round-y: 5px;"
          >
            <VuePDF
              ref="VPDF"
              :pdf="pdf"
              :page="showAllPages ? p : clamp(fPage, 1, pages || 1)"
              :fitParent="zoom === -1"
              :scale="zoom === -1 ? null : zoom"
              :rotation
              :text-layer="!disableLayers"
              :annotation-layer="!disableLayers"
              :watermark-text="autoWatermark"
              @progress="onProgress"
              @annotation="onAnnotation"
              @annotation-loaded="onAnnotationLoaded"
              @loaded="(ev) => onLoaded( ev, showAllPages ? p : clamp(fPage, 1, pages || 1) )"
              class="text-center"
            >
              <div class="w-full h-full flex items-center justify-center">Carregando....</div>
            </VuePDF>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, nextTick, onMounted, watch } from 'vue'
import { useResizeObserver, watchDebounced, useFullscreen, useMagicKeys, whenever, useWindowSize, useScroll } from '@vueuse/core'
import { clamp, debounce } from '@/common/util'
import { useAuthStore, useUsuariosStore } from '@/stores'
import { jsPDF } from 'jspdf'
import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib'

import UiAlert from '@/components/ui/alert.vue'
import UiDropdown from '@/components/ui/dropdown.vue'
import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiInput from '@/components/ui/input.vue'
import UiLoader from '@/components/ui/loader.vue'

import PdfToolbar from './toolbar.vue'

// PDF
import { VuePDF, usePDF } from '@tato30/vue-pdf'
import '@tato30/vue-pdf/style.css'

import type { LoaderPDFType } from '@/types/types'

const auth = useAuthStore()

const props = defineProps<{
  name?: string
  source: string
  allowPrinting?: boolean
  allowDownload: boolean
  watermark?: boolean
  watermarkRules?: {
    username?: boolean
    email?: boolean
    company?: boolean
    unit?: boolean
    department?: boolean
    position?: boolean
    dateTime?: boolean
    ip?: boolean
  }
  customWatermark?: string | string[]
  isClass?: boolean
}>()

const { width: winW } = useWindowSize()

const password = ref('')

const passwordManager = ref({
  requiresPassword: false,
  password: '',
  wrong: false,
  submit: null as (() => void) | null
})

const onPassword = async (updatePassword, reason) => {
  passwordManager.value.requiresPassword = true
  passwordManager.value.wrong = reason === 2

  const result = await new Promise((resolve) => {
    const handlePasswordSubmit = () => {
      resolve(passwordManager.value.password)
    }

    passwordManager.value.submit = handlePasswordSubmit
  })

  passwordManager.value.requiresPassword = false
  nextTick(() => {
    updatePassword(result as string)
  })
}

// Vue-PDF
const { pdf, pages, getPDFDestination, download, print } = usePDF(props.source, {
  password: password.value,
  onError: (reason) => {
    console.error(`PDF loading error: ${reason}`)
  },
  // onProgress: ({ loaded, total }) => {
  //   // console.log(`Progress: ${loaded} of ${total}`)
  // },
  onPassword
})

const VPDF = ref<any>([])
const page = ref(1)

const pdfWrapper = ref<HTMLDivElement>()
const contentWrapper = ref()
const pdfContainer = ref<HTMLElement>()
const timeFix = ref(0)

const fPage = ref(1)
const maxPageReached = ref(1)
const pageReady = ref(false)
const toolbarRef = ref()
const status = ref<LoaderPDFType>('initial')
const src = ref()
const render = ref(true)
const showAllPages = ref(false)
const currentVisiblePage = ref(1)

const baseWidth = ref(800)
const zoom = ref(-1)
const rotation = ref(0)
const isLoading = ref(false)

const userIp = ref('')
const passwordDialogRef = ref()

const getMaxVisiblePage = () => {
  if (!pdfContainer.value) return 1
  const containerRect = pdfContainer.value.getBoundingClientRect()
  const viewportHeight = pdfContainer.value.clientHeight

  let maxPage = 1
  for (let p = 1; p <= pages.value; p++) {
    const pageEl = pdfContainer.value.querySelector(`#pdf-page-${p}`)
    if (pageEl) {
      const canvas = pageEl.querySelector('canvas')
      if (canvas) {
        const pageRect = canvas.getBoundingClientRect()
        const relativeTop = pageRect.top - containerRect.top
        const relativeBottom = pageRect.bottom - containerRect.top
        const isVisible = relativeBottom > 0 && relativeTop < viewportHeight
        if (isVisible) {
          maxPage = p
        }
      }
    }
  }

  return maxPage
}

const {
  isFullscreen,
  toggle
} = useFullscreen(contentWrapper)

const disableLayers = ref(false)
const downloadInProgress = ref(false)

const onLoaded = (ev?: any, p?: any) => {}

const showPdfContainer = ref(true)

const watermarkStyle = {
  columns: 4,
  rows: 4,
  rotation: 45,
  fontSize: 18,
  color: { r: 211, g: 210, b: 211 },
  opacity: 0.4
}

const triggerDownload = (bytes: Uint8Array, filename: string) => {
  const payload = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
  const blob = new Blob([payload], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
    document.body.removeChild(anchor)
  }, 50)
}

const drawCanvasWatermark = (ctx: CanvasRenderingContext2D, width: number, height: number, scale: number, text: string) => {
  const { columns, rows, rotation, fontSize, color, opacity } = watermarkStyle
  const total = columns * rows

  ctx.font = `${fontSize * scale}px Trebuchet MS`
  ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  for (let i = 0; i < total; i++) {
    const x = (i % columns) / columns * width + width / (columns * 2)
    const y = Math.floor(i / columns) / rows * height + height / (rows * 2)

    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(-rotation * (Math.PI / 180))
    ctx.fillText(text, 0, 0)
    ctx.restore()
  }
}

// Adiciona o watermark de forma vetorial dentro do PDF original (lossless)
const downloadWithWatermarkLossless = async (): Promise<boolean> => {
  const loadingTask = pdf.value
  if (!loadingTask) return false

  const docProxy = await loadingTask.promise
  const originalBytes = await docProxy.getData()

  let pdfDoc: PDFDocument
  try {
    pdfDoc = await PDFDocument.load(originalBytes)
  } catch {
    return false
  }

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const text = autoWatermark.value
  const { columns, rows, rotation, fontSize, color, opacity } = watermarkStyle

  for (const page of pdfDoc.getPages()) {
    const { width, height } = page.getSize()

    for (let i = 0; i < columns * rows; i++) {
      const col = i % columns
      const row = Math.floor(i / columns)
      const x = col / columns * width + width / (columns * 2)
      const y = row / rows * height + height / (rows * 2)

      page.drawText(text, {
        x: x - font.widthOfTextAtSize(text, fontSize) / 2,
        y: y - font.heightAtSize(fontSize) / 2,
        size: fontSize,
        font,
        color: rgb(color.r / 255, color.g / 255, color.b / 255),
        opacity,
        rotate: degrees(rotation)
      })
    }
  }

  triggerDownload(await pdfDoc.save({ useObjectStreams: false }), `${props.name || 'document'}.pdf`)
  return true
}

// Fallback: renderiza as páginas em alta resolução e as recompõe com o watermark
const downloadWithWatermarkRaster = async (): Promise<boolean> => {
  const loadingTask = pdf.value
  if (!loadingTask) return false

  const docProxy = await loadingTask.promise
  const targetDpi = 300
  const scale = targetDpi / 72
  const MAX_PIXELS = 4000

  let out: jsPDF | null = null

  for (let i = 1; i <= docProxy.numPages; i++) {
    const page = await docProxy.getPage(i)
    const baseViewport = page.getViewport({ scale: 1 })
    const totalRotation = (rotation.value + baseViewport.rotation) % 360
    const renderViewport = page.getViewport({ scale, rotation: totalRotation })

    const width = Math.min(Math.floor(renderViewport.width), MAX_PIXELS)
    const height = Math.min(Math.floor(renderViewport.height), MAX_PIXELS)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return false

    const renderScale = width / renderViewport.width
    const actualViewport = page.getViewport({ scale: scale * renderScale, rotation: totalRotation })

    await page.render({ canvas, canvasContext: ctx, viewport: actualViewport }).promise

    drawCanvasWatermark(ctx, width, height, scale * renderScale, autoWatermark.value)

    const ptsW = actualViewport.width / (scale * renderScale)
    const ptsH = actualViewport.height / (scale * renderScale)

    if (!out) {
      out = new jsPDF({
        unit: 'pt',
        format: [ptsW, ptsH],
        orientation: ptsW > ptsH ? 'landscape' : 'portrait',
        compress: true
      })
    } else {
      out.addPage([ptsW, ptsH], ptsW > ptsH ? 'landscape' : 'portrait')
    }

    out.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, ptsW, ptsH)
  }

  if (!out) return false
  out.save(`${props.name || 'document'}.pdf`)
  return true
}

const customDownload = async () => {
  if (!pdf.value || !(pages.value || 0) || !props.allowDownload || downloadInProgress.value)
    return

  downloadInProgress.value = true

  try {
    if (!(await downloadWithWatermarkLossless()))
      await downloadWithWatermarkRaster()
  } catch (error) {
    console.error('[pdfViewer] Erro ao gerar download com watermark:', error)
  } finally {
    downloadInProgress.value = false
  }
}

const {
  ctrl_p,
  ctrl_s,
  alt_enter,
  arrowright,
  arrowleft,
  ctrl_arrowright,
  ctrl_arrowleft,
  ctrl_equal,
  ctrl_minus
} = useMagicKeys({
  passive: false,
  onEventFired: (e) => {
    if ([ctrl_p, ctrl_s, alt_enter, arrowright, arrowleft, ctrl_arrowright, ctrl_arrowleft, ctrl_equal, ctrl_minus].some(key => key.value))
      e.preventDefault()
  },
  target: contentWrapper.value
})

const savePdf = () => {
  if (props.allowDownload) {
    if (autoWatermark.value) {
      customDownload()
      return
    } else {
      download(`${props.name || 'document'}.pdf`)
    }
  }
}

whenever(ctrl_p, () => print(800, 'pdf'))
whenever(ctrl_s, () => savePdf())
whenever(alt_enter, () => toggle())
whenever(ctrl_arrowright, () => nextTick(() => toolbarRef.value?.lastPage()))
whenever(ctrl_arrowleft, () => nextTick(() => toolbarRef.value?.firstPage()))
whenever(arrowright, () => toolbarRef.value?.nextPage())
whenever(arrowleft, () => toolbarRef.value?.previousPage())
whenever(ctrl_equal, () => setZoom(zoom.value + 0.1))
whenever(ctrl_minus, () => setZoom(zoom.value - 0.1))

// Redesenha o PDF quando a largura da janela mudar
var lastTimeout = 0
watch(winW, (n, o) => {
  if (n === o)
    return

  clearTimeout(lastTimeout)

  if (zoom.value === -1 && VPDF.value) {
    for (let i = 1; i <= (pages.value || 1); i++) {
      VPDF.value?.[i]?.cancel()

      lastTimeout = window.setTimeout(() => {
        VPDF.value?.[i]?.reload()
      }, 100)
    }
  }
})

const autoWatermark = computed(() => {
  if (props.customWatermark) return Array.isArray(props.customWatermark) ? props.customWatermark.join(' - ') : props.customWatermark

  const fields : any[] = []

  if (props.watermark) {
    const { username, email, company, unit, department, position, dateTime, ip } = props?.watermarkRules || {}

    if (username) fields.push(auth.userData?.nomeCompleto || auth.persistentInfo?.name)
    if (email) fields.push(auth.userData?.email || auth.payload.email)
    if (company) fields.push(useUsuariosStore().partnerList.find(p => p.tenantId === auth.getActiveTentant)?.accountName)
    if (unit) fields.push(auth.userData.unidadeDeNegocioNome || auth.persistentInfo?.unidade?.name)
    if (department) fields.push(auth.userData?.departamentoNome || auth.persistentInfo?.department)
    if (position) fields.push(auth.persistentInfo.role)

    if (dateTime) fields.push((new Date().toLocaleDateString('pt-br') + ' ' + new Date().toLocaleTimeString('pt-br')))
    if (ip && userIp.value) fields.push(`IP:${userIp.value}`)
  }

  return fields.filter(f => f).join(' - ')
})

useScroll(pdfContainer, {
  onStop() {
    // Verifica qual é o número da maior página visível
    const maxVisible = getMaxVisiblePage()
    if (showAllPages.value) {
      currentVisiblePage.value = maxVisible
      page.value = maxVisible
    }
  }
})

const toggleViewMode = () => {
  const currentPage = showAllPages.value ? currentVisiblePage.value : page.value
  showAllPages.value = !showAllPages.value

  nextTick(() => {
    if (!showAllPages.value) {
      page.value = currentPage
      fPage.value = currentPage
    } else {
      scrollToPage(currentPage)
    }
  })
}

const pageActions = ref<{
  id: string
  fieldName: string
  type: string
  dest: any
  action?: any
  url?: any
}[]>([])

const onAnnotationLoaded = (e) => {
  if (!e || !Array.isArray(e))
    return

  e.forEach(async (annotation) => {
    if (!annotation.id)
      return

    const ann = {
        id: annotation.id,
        fieldName: annotation.fieldName,
        type: annotation.type,
        dest: (await getPDFDestination(annotation.dest) || null),
        action: annotation?.action,
        url: annotation?.url
    }

    const existingIndex = pageActions.value.findIndex(a => a.id === annotation.id)
    if (existingIndex !== -1) {
      pageActions.value[existingIndex] = ann
    } else {
      pageActions.value.push(ann)
    }
  })
}

const onAnnotation = async (e) => {
  if (e?.type === 'form-button') {
    if (e.data.fieldName) {
      const { action, dest, url } = pageActions.value?.find(a => a.fieldName === e.data.fieldName) || {}

      if (action === 'NextPage') page.value++
      else if (action === 'PrevPage') page.value--
      else if (action === 'FirstPage') page.value = 1
      else if (action === 'LastPage') page.value = pages.value

      if (dest?.pageIndex > -1) {
        if (showAllPages.value) {
          scrollToPage(dest.pageIndex + 1)
        } else {
          page.value = dest.pageIndex + 1
        }
      }

      if (url) {
        if (window.location.hostname !== new URL(url).hostname) {
          window.open(url, '_blank')
        }
      }
    }
    return
  }

  if (e?.type === 'internal-link' && e.data?.referencedPage) {
    if (showAllPages.value) {
      scrollToPage(Number(e.data.referencedPage))
      return
    }

    page.value = Number(e.data.referencedPage)
  }

  else if (e?.type === 'link' && e.data?.url) {
    const url = e.data.url
    if (window.location.hostname !== new URL(url).hostname) {
      window.open(url, '_blank')
    }
  }
}

const scrollToPage = (pageNum: number) => {
  if (!showAllPages.value || pageNum < 1 || pageNum > pages.value) return

  const targetPage = pdfContainer.value?.querySelector<HTMLElement>(`#pdf-page-${pageNum}`)
  if (targetPage) {
    nextTick(() => {
      targetPage.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }
}

const savedPage = ref(0)
const saveProgress = debounce((pageNum: number) => { savedPage.value = pageNum }, 1000)

watch([page, currentVisiblePage], ([pageVal, visiblePageVal]) => {
  saveProgress(showAllPages.value ? visiblePageVal : pageVal)
})

watch(showAllPages, (newVal) => {
  if (!newVal && (!page.value || page.value < 1)) {
    page.value = 1
  }
})

const onProgress = (progress: {loaded: number; total: number}) => {
  if (progress.loaded !== progress.total && status.value !== 'loading') status.value = 'loading'
  else if (progress.loaded === progress.total) status.value = 'success'
}

watchDebounced(
  () => page.value,
  (val) => {
    if (showAllPages.value) {
      // scrollToPage(val)
      return
    }

    fPage.value = clamp(val, 1, pages.value)
    page.value = fPage.value

    if (val > maxPageReached.value) maxPageReached.value = fPage.value
  },
  { debounce: 200, maxWait: 2000 }
)

useResizeObserver(pdfWrapper, (entries) => {
  const { width: cWidth } = entries?.[0].contentRect || {}
  if (cWidth) debounce(() => baseWidth.value = cWidth, 200)()
})

onBeforeUnmount(() => {
  pageReady.value = false
  status.value = 'initial'
  passwordDialogRef.value?.reset()
})

onBeforeMount(() => {
  timeFix.value = Date.now()
  src.value = props.source

  if (props.watermarkRules?.ip) {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => {
        userIp.value = data?.ip
      })
  }
})

onMounted(() => {
  if (pdfWrapper.value) {
    baseWidth.value = pdfWrapper.value.clientWidth

    useResizeObserver(pdfWrapper, (entries) => {
      const entry = entries[0]
      if (entry?.contentRect) {
        baseWidth.value = entry.contentRect.width
      }
    })
  }
})

const jumpToPercent = async (percent: number) => {
  page.value = Math.round(clamp((percent / 100) * pages.value, 1, pages.value))
}

const setPage = (pageNum: number) => {
  if (showAllPages.value) {
    scrollToPage(pageNum)
    return
  }

  page.value = clamp(pageNum, 1, pages.value)
}

const setZoom = (newZoom: number) => {
  // Automático
  if (newZoom === -1) {
    zoom.value = -1

    return
  }

  zoom.value = Math.max(0.1, Math.min(newZoom, 4))
}

const wrapperStyle = computed(() => ({
  height: '100vh',
  width: isFullscreen.value ? '100vw' : '',
  overflow: 'hidden'
}))

defineExpose({
  pageCount: computed(() => pages.value),
  page: computed(() => page.value),
  jumpToPercent,
  setPage,
  setZoom,
  scrollToPage
})
</script>

<style lang="scss" scoped>
.vue-pdf-embed__page {
  margin-bottom: 1rem;
  scroll-margin-top: 20px;
}

.p-wrapper {
  scroll-margin-top: 20px;
}
</style>

<style lang="scss">
.hiddenCanvasElement {
  display: none
}

.p-wrapper > div > * {
  margin: 0 auto;
}
</style>